
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Checks if a recipe has been used in the menu within the specified number of days
 * @param recipeId The recipe ID to check
 * @param menu The weekly menu object
 * @param currentDay The current day being checked
 * @param lookbackDays Number of days to look back for duplicates
 * @returns boolean indicating if the recipe has been used recently
 */
export function isRecipeUsedRecently(
  recipeId: string,
  menu: any,
  currentDay: string,
  lookbackDays: number = 3
): boolean {
  if (!menu?.days) return false;
  
  const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const currentDayIndex = dayOrder.indexOf(currentDay);
  
  if (currentDayIndex === -1) return false;
  
  // Calculate the start day index for lookback
  const startDayIndex = Math.max(0, currentDayIndex - lookbackDays);
  
  // Check if the recipe is used in the lookback period
  for (let i = startDayIndex; i < currentDayIndex; i++) {
    const checkDay = dayOrder[i];
    const dayMenu = menu.days[checkDay];
    
    // Check all meal types in this day
    for (const mealType in dayMenu) {
      if (Array.isArray(dayMenu[mealType])) {
        if (dayMenu[mealType].some((meal: any) => meal.recipeId === recipeId)) {
          return true;
        }
      }
    }
  }
  
  return false;
}

/**
 * Gets recipe recommendations that haven't been used recently in the menu
 * @param availableRecipes Array of available recipes
 * @param menu The weekly menu object
 * @param day The current day to add recipes to
 * @param count Number of recipes to recommend
 * @param mealType Type of meal (breakfast, lunch, dinner, etc)
 * @returns Array of recommended recipes
 */
export function getVariedRecipeRecommendations(
  availableRecipes: any[],
  menu: any,
  day: string,
  count: number = 3,
  mealType?: string
): any[] {
  if (!availableRecipes?.length) return [];
  
  // Filter for meal type if specified
  let eligibleRecipes = availableRecipes;
  
  if (mealType) {
    eligibleRecipes = availableRecipes.filter(recipe => 
      recipe.tags && 
      Array.isArray(recipe.tags) && 
      recipe.tags.some(tag => 
        tag.toLowerCase().includes(mealType.toLowerCase()) ||
        (mealType === 'breakfast' && tag.toLowerCase().includes('desayuno')) ||
        (mealType === 'lunch' && (tag.toLowerCase().includes('almuerzo') || tag.toLowerCase().includes('comida'))) ||
        (mealType === 'dinner' && tag.toLowerCase().includes('cena')) ||
        (mealType.includes('snack') && tag.toLowerCase().includes('snack'))
      )
    );
    
    // If no recipes match the meal type, fall back to all recipes
    if (eligibleRecipes.length < count) {
      eligibleRecipes = availableRecipes;
    }
  }
  
  // Filter out recipes used in the last 3 days
  const freshRecipes = eligibleRecipes.filter(recipe => 
    !isRecipeUsedRecently(recipe.id, menu, day, 3)
  );
  
  // If we don't have enough fresh recipes, add some from the full list
  if (freshRecipes.length < count) {
    // Sort recipes by how recently they were used
    const sortedRecipes = [...eligibleRecipes].sort((a, b) => {
      const aUsed = isRecipeUsedRecently(a.id, menu, day, 2) ? 1 : 0;
      const bUsed = isRecipeUsedRecently(b.id, menu, day, 2) ? 1 : 0;
      return aUsed - bUsed; // Prioritize recipes not used in the last 2 days
    });
    
    // Add recipes not in freshRecipes until we reach count
    for (const recipe of sortedRecipes) {
      if (!freshRecipes.some(r => r.id === recipe.id)) {
        freshRecipes.push(recipe);
        if (freshRecipes.length >= count) break;
      }
    }
  }
  
  // Randomize for additional variety and return up to 'count' recipes
  return freshRecipes
    .sort(() => 0.5 - Math.random())
    .slice(0, count);
}

